import {ReactNode} from 'react';

import {TABLE_ROW, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {computeCustomRowLabel} from 'notebook/tables/store/logic/TableIdentityLogic';
import {
  AxisHeaderModel,
  createAxisHeaderIdentitySelector,
} from 'notebook/tables/store/models/AxisHeaderModel';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createDebuggableSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * The model for a single row header cell, keyed by row index. Rendered by `TableRow`.
 *
 * The row axis carries none of the column header's constraint-driven metadata (no rename, no
 * column type icon, no custom column highlight), so beyond the shared {@link AxisHeaderModel} this
 * holds the row axis's own permissions. Row-specific values that `TableRow` still passes to
 * `TableAxisCell` as props — the row's data, status, and in-view state — belong here as they are
 * migrated.
 */
export interface RowHeaderModel extends AxisHeaderModel {
  dim: TableDimension.ROW;
  /**
   * Whether this cell can toggle the header row. Only the first row's header offers the toggle, and
   * only when dimension edits are allowed and the legacy header toggle is not disabled.
   */
  canToggleHeader: boolean;
  /** Whether rows can be inserted above or below this one. */
  canInsertDims: boolean;
  /** Whether rows can be deleted. */
  canDeleteDims: boolean;
  /** Whether the whole table can be deleted. */
  canDeleteTable: boolean;
}

/** Creates a selector for the row header at `index` against the table store. */
export function createRowHeaderModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  index: number
): (store: TableEditableStore) => RowHeaderModel {
  return createDebuggableSelector.withTypes<TableEditableStore>()(
    [
      createAxisHeaderIdentitySelector(tableSelector, TABLE_ROW, index),
      (store) => computeCustomRowLabel(tableSelector(store).props.table.rowLabels, index),
      (store) => tableSelector(store).dimensions.canModifyDims,
      // `props` holds TableEditable's raw props, so the `??` default here repeats the one
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      (store) => tableSelector(store).props.disableLegacyHeader ?? false,
      (store) => tableSelector(store).dimensions.canModifyRowDim,
      // The single-input view renders no row headers, so the `!isSingleInput` gate that
      // `TableEditable._getTRProps` applied to row deletion cannot be reached from here.
      (store) => tableSelector(store).dimensions.canRemoveRows,
      (store) => tableSelector(store).canDeleteTable,
    ],
    (
      identity,
      customLabel,
      canModifyDims,
      disableLegacyHeader,
      canModifyRowDim,
      canRemoveRows,
      canDeleteTable
    ): RowHeaderModel => ({
      ...identity,
      dim: TABLE_ROW,
      isHeaderCell: false,
      customLabel,
      canToggleHeader: index === 0 && canModifyDims && !disableLegacyHeader,
      canInsertDims: canModifyRowDim,
      canDeleteDims: canRemoveRows,
      canDeleteTable,
    })
  );
}

/**
 * A {@link RowHeaderModel} for a row header rendered outside of any table store, whose label comes
 * directly from its props rather than from a table. Such a header labels no table, so it permits
 * none of the dimension actions.
 */
export function makeStandaloneRowHeaderModel({
  index,
  customLabel = null,
}: {
  index: number;
  customLabel?: ReactNode;
}): RowHeaderModel {
  return {
    dim: TABLE_ROW,
    index,
    isGhostCell: false,
    isHeaderCell: false,
    customLabel,
    canToggleHeader: false,
    canInsertDims: false,
    canDeleteDims: false,
    canDeleteTable: false,
  };
}
